import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ICountry, IDepartament, ILanguage, IMunicipality, IProvince, IUser } from '@core/models';
import { TermsPrivacyPolicy } from '../../terms-privacy-policy/terms-privacy-policy.component';
import moment from 'moment';
import { Language, Ubication, User } from '@core/services';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPasswordValidator } from '@shared/validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, lastValueFrom, startWith } from 'rxjs';
import { touchControls } from '@shared/utils';
import { RegisterSuccessModal } from '../register-success-modal/register-success-modal.component';
import { TranslateModule } from '@ngx-translate/core';

import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';

/** constante que contiene el código de colombia*/
const COLOMBIA_CODE = 'COL';

/**
 * ### **RegisterComponent**
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar el formulario de registro y de enviar los datos al servidor.
 */
@Component({
  selector: 'app-register-form-modal',
  imports: [TranslateModule, ReactiveFormsModule, NgbDatepickerModule, TermsPrivacyPolicy],
  templateUrl: "./register-form-modal.component.html",
  styleUrl: "./register-form-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterFormModal {
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(User);
  private readonly ubication = inject(Ubication);
  private readonly language = inject(Language);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Variable que recibe el modal de un componente padre o superior a el.
   */
  readonly modalRef = input.required<any>();

  /** variable que recibe los datos del usuario de un componente padre */
  readonly user = input<IUser | null>(null);

  /**
   * Variable que guardara y emitira si un usuario se ha registrado.
   */
  readonly userRegistered = output<any>();

  /**
   * Variable que controlara y guardar los datos del formulario de registro de usuario.
   */
  protected readonly registerForm = signal<FormGroup | null>(null);

  /**
   * Variable que guardara el estado de aceptación de los terminos y condiciones.
   */
  protected readonly acceptTerms = signal<FormControl>(
    new FormControl(false, [Validators.requiredTrue]),
  );

  /** variable que guardara la lista de paises*/
  protected readonly countries = signal<ICountry[]>([]);
  /** variable que guardara la lista de departamentos */
  protected readonly departments = signal<IDepartament[]>([]);
  /** Arreglo que guardará la lista de municipios */
  protected readonly municipalities = signal<IMunicipality[]>([]);
  /** Arreglo que guardara la lista de idiomas */
  protected readonly languages = signal<ILanguage[]>([]);
  /** Variable que guardará el estado de existencia de un usuario. */
  protected readonly userExists = signal<boolean>(false);
  /** Variable que guardara la fecha de cumpleaños del usuario a registrar */
  private birthdate!: any;
  /** Variable que contendra el prefijo del número telefónico */
  private prefix!: string;
  /**  variable que hace referencia a una persona que es de colombia (True) o no (False). */
  protected readonly IS_COLOMBIA = signal<boolean>(false);

  /** decorador que sirve para representan un componente obteniendo los elementos nativos y directivas */
  private readonly termsPrivacy = viewChild(TermsPrivacyPolicy);

  constructor() {
    this.getCountries();
    this.getLanguages();
  }

  /**
   * Función nativa de angular que se activa una vez se empieza a cargar el componenten, en este caso
   * ejecuta la funcion this.createForm()
   */
  ngOnInit(): void {
    this.createForm();
  }

  /**
   * Función nativa de angular que se ctiva una vez ha detectado un cambio sobre las variables de tipo @Input
   * @param changes parametro que detecta los cambios.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && changes.user.currentValue) {
      if (this.user()) {
        this.acceptTerms()?.setValue(true);
      }
    }
  }

  /**
   * Función que obtiene y le da formato a la fecha de cuumpleaños del usuario a registrar.
   * @param birthdate fecha de cumpleaños del usuario.
   */
  private getBirthdate(birthdate: string): {
    year: number;
    month: number;
    day: number;
  } {
    const momentBirthdate = moment(birthdate, 'YYYY-MM-DD');
    return {
      year: momentBirthdate.year(),
      month: momentBirthdate.month(),
      day: momentBirthdate.day(),
    };
  }

  /**
   * funcion encargada de crear el formulario con sus respectivos controls para la debida recaudacion de los datos.
   */
  private createForm(): void {
    const user = this.user();
    if (user) {
      this.birthdate = user.birthdate;
    }
    const registerForm = this.fb.group(
      {
        username: [user ? user.username : null, Validators.required],
        last_name: [user ? user.last_name : null, Validators.required],
        country: [user && user.country ? user.country.id : null, Validators.required],
        first_name: [user ? user.first_name : null, Validators.required],
        region: [user ? user.region : null],
        state: [user ? user.state : null],
        city: [user ? user.city : null],
        re_password: [null, !user ? Validators.required : []],
        email: [
          user ? user.email : null,
          [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          ],
        ],
        is_superuser: [user ? user.is_superuser : false],
        password: [null, !user ? Validators.required : []],
        language: [user ? user.language : null, Validators.required],
        phone_code: [user ? user.phone_code : null, [Validators.required]],
        phone: [
          user ? user.phone : null,
          [Validators.required, Validators.pattern(/^-?(0|[1-9]\d*)?$/)],
        ],
        gender: [user ? user.gender : null, Validators.required],
        birthdate: [user ? this.getBirthdate(user.birthdate) : null, Validators.required],
      },
      {
        validator: ConfirmPasswordValidator.MatchPassword,
      },
    );

    this.registerForm.set(registerForm);
    this.valueChanges();
  }

  /**
   * Función encargada de detectar si han habido cambios en el formulario de registro, en este caso lo que se hace
   * es suscribirse a varios elementos del formulario en los cuales puede haber algún cambio y afectar a otros elementos.
   */
  private valueChanges(): void {
    this.registerForm()
      ?.get('country')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.registerForm()?.get('country')?.value),
      )
      .subscribe((value) => {
        const countries = this.countries();
        if (countries) {
          const country = countries.find((c) => c.id === value);
          const form = this.registerForm();
          if (country) {
            form?.get('phone_code')?.setValue(country.phone_code);
            if (country.alfa_three === COLOMBIA_CODE) {
              this.IS_COLOMBIA.set(true);
              // this.registerForm
              //   .get('state')
              //   ?.setValidators(Validators.required);
              form?.get('state')?.setValidators(null);
              // this.registerForm.get('city')?.setValidators(Validators.required);
              form?.get('city')?.setValidators(null);
              form?.updateValueAndValidity();
            } else {
              this.IS_COLOMBIA.set(false);
              form?.get('state')?.setValidators(null);
              form?.get('city')?.setValidators(null);
              form?.updateValueAndValidity();
            }
          } else {
            form?.get('phone_code')?.setValue(null);
          }
        }
        if (value) {
          this.getDepartments(value);
        }
      });

    this.registerForm()
      ?.get('state')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.registerForm()?.get('state')?.value),
        filter((value) => !!value),
      )
      .subscribe((value) => {
        console.log('value: ' + value);
        this.getMunicipalities(value);
      });

    // this.registerForm
    //   .get('username')
    //   ?.valueChanges.pipe(takeUntil(this.unsubscribe))
    //   .subscribe((value) => {
    //     this.validUniqueUser(value);
    //   });

    // this.registerForm
    //   .get('email')
    //   ?.valueChanges.pipe(takeUntil(this.unsubscribe))
    //   .subscribe((value) => {
    //     this.validUniqueEmail(value);
    //   });
  }

  /**
   * Función encargada de ejecutar el registro de usuario, para esto el mismo debio acpetar los terminos y condiciones y el formulario debe
   * ser valido, en caso de haber un usuario existente el metodo detecta que esta editando la infromacion del mismo y
   * no esta agregando un usuario.
   */
  protected registerUser(): void {
    const form = this.registerForm();
    if (!form) return;

    if (form?.invalid) {
      touchControls(form);
      this.acceptTerms().markAsTouched();
      return;
    }

    if (this.acceptTerms().valid && form?.valid && !this.userExists()) {
      if (this.user()) {
        const registerForm = form?.getRawValue();
        const birthdate = registerForm['birthdate'];
        registerForm['birthdate'] = `${birthdate.year}-${birthdate.month}-${birthdate.day}`;
        delete registerForm['password'];
        delete registerForm['re_password'];
        this.editUser(registerForm);
      } else {
        const registerForm = form.getRawValue();
        const birthdate = registerForm['birthdate'];
        registerForm['birthdate'] = `${birthdate.year}-${birthdate.month}-${birthdate.day}`;
        this.addUser(registerForm);
      }
    } else {
      touchControls(form);
      this.acceptTerms().markAsTouched();
    }
  }

  /**
   * Función encargado de abrir el modal de Terminos y Condiciones (TC).
   * @param terms Parametro que contiene el modal de TC.
   */
  protected openTermsOrPrivacy(terms: boolean): void {
    this.termsPrivacy()?.open(terms);
  }

  /**
   * Función encargada de agregar el usuario, en caso dde ya existir cambia el estado de this.userExists a true.
   * @param user Objeto con la información del usuario a registrar.
   */
  private async addUser(user: IUser): Promise<void> {
    try {
      const response = await lastValueFrom(this.userService.add(user));
      this.modalRef()?.dismiss();
      this.userRegistered.emit(response);
      this.showRegisterSuccess();
    } catch (error) {
      console.error(error);
      this.userExists.set(true);
    }
  }

  /**
   * Función encargada modificar o editar la información del usuario.
   * @param user Objeto con la información a editar el usuario.
   */
  private async editUser(user: IUser): Promise<void> {
    try {
      const response = await lastValueFrom(this.userService.edit(Number(this.user()?.id), user));
      this.modalRef()?.dismiss();
      // this.userRegistered.emit(response);
      this.showRegisterSuccess();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Función encargada de obtener la lista de paises y enviarselos al formulario,
   * para esto hace uso de la funcion getCountries del servicio UbicationService.
   */
  private async getCountries(): Promise<void> {
    const countries = await lastValueFrom(this.ubication.getCountries());
    this.countries.set(countries);
    if (this.countries() && this.registerForm) {
      this.registerForm()?.get('country')?.setValue(this.registerForm()?.get('country')?.value);
    }
  }

  /**
   * Función encargada de obtener los departamentos de un país en específico.
   * @param country Parametro que recibe el id del pais.
   */
  private async getDepartments(country: number): Promise<void> {
    const departments = await lastValueFrom(this.ubication.getDepartmentsAll());
    this.departments.set(departments);
  }

  /**
   * Función encargada de obtener los municipios de un departamento en específico.
   * @param department Parametro que recibe el identificador del municipio.
   */
  private async getMunicipalities(department: number): Promise<void> {
    const municipalities = await lastValueFrom(this.ubication.getNewMunicipalities(department));
    this.municipalities.set(municipalities);
  }

  /**
   * Función encargada de obtener los idiomas existen actualmente.
   */
  private async getLanguages(): Promise<void> {
    const languages = await lastValueFrom(this.language.getLanguages());
    this.languages.set(languages);
  }

  /**
   * Función encargada de validar si el nombre usuario no esta repetido, para esto se apoya del
   * servicio UserService para darle un estado repsectivo a userExists y determinar si el nombre esa ocupado.
   * @param username Parametro que recibira el nombre de usuario.
   */
  protected async validUniqueUser(username: string): Promise<void> {
    try {
      const response = await lastValueFrom(this.userService.validUniqueUSer(username));

      const user = this.user();
      if (user) {
        if (user.username === username.trim()) {
          this.userExists.set(false);
        } else {
          this.userExists.set(true);
        }
      } else {
        this.userExists.set(true);
      }
    } catch (error) {
      console.error(error);
      this.userExists.set(false);
    }
  }

  /**
   * Función encargada de validar que el email sea unico, para esto hace uso del USerService y darle un estado repsectivo a userExists y
   * determinar si el email esta ocupado.
   * @param email Parametro que recibira el email ingresado por el usuario a registrarse.
   */
  protected async validUniqueEmail(email: string): Promise<void> {
    try {
      const response = await lastValueFrom(this.userService.validUniqueEmail(email));
      const user = this.user();
      if (user) {
        if (user.email === email.trim()) {
          this.userExists.set(false);
        } else {
          this.userExists.set(true);
        }
      } else {
        this.userExists.set(true);
      }
    } catch (error) {
      console.error(error);
      this.userExists.set(false);
    }
  }

  /**
   * Función encargada de abrir el modal de confirmación de registro de usuario
   * además de enviarle la informacion del mismo.
   */
  private showRegisterSuccess(): void {
    const config = {
      data: {
        edited: !!this.user(),
      },
    };
    const dialogRef = this.dialog.open(RegisterSuccessModal, config);
  }
}
